import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import type { RootState } from '../../store';
import type { Workflow } from '../../store/project/projectSlice';
import { setWorkflowSettings } from '../../store/workflow/workflowSlice';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { SCHEDULE_PRESETS, WorkflowSettings, describeSchedule } from '../../types/workflowSettings';
import { normalizeFolder } from '../../utils/workflowFolders';
import { effectiveTimeZone } from '../../utils/timeZone';
import { useTranslation } from 'react-i18next';
import ParamsEditor from './panels/PropertyPanel/ParamsEditor';

export interface WorkflowMeta { id: string; name: string; description?: string; folder: string }

interface Props {
  /** 새로 만들 때는 껍데기(id 없음) — 실행 설정은 캔버스가 생긴 뒤에 정하므로 이름·폴더·설명만 받는다 */
  workflow: Workflow;
  isNew: boolean;
  /** 이 프로젝트에 있는 폴더 경로 — 이동 대상 선택지 */
  folders: string[];
  /** 이름·폴더·설명 저장(탐색기가 서버에 쓴다). 실행 설정은 이 창이 캔버스에 넣는다 */
  onSave: (meta: WorkflowMeta) => void;
  onClose: () => void;
}

// 줄·소제목은 컴포넌트 **밖**에 둔다 — 안에서 만들면 글자 하나 칠 때마다 새 컴포넌트가 되어 입력칸이 초점을 잃는다
const Row = ({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) => (
  <div className="grid grid-cols-[130px_1fr] items-start gap-2 py-1.5">
    <div className="text-xs text-gray-600 pt-1">{label}</div>
    <div className="min-w-0">{children}{hint && <div className="text-[0.6875rem] text-gray-400 mt-0.5">{hint}</div>}</div>
  </div>
);
const Section = ({ children }: { children: React.ReactNode }) => (
  <div className="text-xs font-semibold text-gray-700 mt-3 mb-1 first:mt-0">{children}</div>
);

/**
 * 워크플로 설정 — 탐색기의 ⚙ 에서 연다. (예전에는 캔버스 위 「설정」 버튼과 두 곳에 나뉘어 있었다)
 *
 *   · 이름·폴더·설명 — 누르는 즉시 서버에 저장된다
 *   · 실행 설정(스케줄·재시도·알림·중간 데이터) — 캔버스 문서에 들어가 Save 때 저장되고 Deploy 때 DAG 인자가 된다.
 *     기본은 실행 주기·시작일만 보이고 나머지는 「상세」로 접는다
 *   · 창 크기는 고정 — 상세를 펼치고 접어도 창이 출렁이지 않고 안쪽만 스크롤한다
 */
const WorkflowSettingsDialog: React.FC<Props> = ({ workflow, isNew, folders, onSave, onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const dispatch = useDispatch();
  const current = useSelector((s: RootState) => s.workflow.settings);
  const openId = useSelector((s: RootState) => s.workflow.current?.id);
  const connections = useSelector((s: RootState) => s.connections.list);
  // 실행 설정은 열린 캔버스의 것 — 탐색기가 먼저 열어 두지만, 혹시 다른 워크플로가 열려 있으면 손대지 않는다
  const runEditable = !isNew && openId === workflow.id;

  const [name, setName] = useState(workflow.name || '');
  const [description, setDescription] = useState(workflow.description || '');
  const [folder, setFolder] = useState(workflow.folder || '');
  const [st, setSt] = useState<WorkflowSettings>({ ...current, notify: { ...current.notify }, staging: { ...current.staging } });
  const [detailOpen, setDetailOpen] = useState(false);

  const patch = (p: Partial<WorkflowSettings>) => setSt((s) => ({ ...s, ...p }));
  const patchN = (p: Partial<WorkflowSettings['notify']>) => setSt((s) => ({ ...s, notify: { ...s.notify, ...p } }));
  const patchS = (p: Partial<WorkflowSettings['staging']>) => setSt((s) => ({ ...s, staging: { ...s.staging, ...p } }));
  const isPreset = SCHEDULE_PRESETS.some((p) => p.value === st.schedule);
  const [custom, setCustom] = useState(!isPreset && !!st.schedule);
  const cronOk = !st.schedule || /^@(hourly|daily|weekly|monthly|yearly|once)$/.test(st.schedule) || st.schedule.trim().split(/\s+/).length === 5;
  const canSave = !!name.trim() && (!runEditable || cronOk);

  const save = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSave) return;
    if (runEditable && JSON.stringify(st) !== JSON.stringify(current)) dispatch(setWorkflowSettings(st));
    onSave({ id: workflow.id, name: name.trim(), description: description || undefined, folder: normalizeFolder(folder) });
  };

  // 목록에 없는 경로를 적으면 그 폴더가 새로 생긴다 — 그래서 select 가 아니라 datalist 다
  const folderOptions = Array.from(new Set([...folders, normalizeFolder(folder)].filter(Boolean)));

  /** 접힌 상세의 요약 — 펼치지 않아도 무엇이 켜져 있는지 보이게 */
  const detailSummary = [
    st.retries ? t('dialog:settings.sumRetries', { n: st.retries }) : t('dialog:settings.sumNoRetry'),
    st.timeoutMin ? t('dialog:settings.sumTimeout', { n: st.timeoutMin }) : null,
    st.notify.onFailure || st.notify.onSuccess ? t('dialog:settings.sumNotify') : t('dialog:settings.sumNoNotify'),
    t('dialog:settings.sumStaging', { mode: t(`dialog:settings.modeShort.${st.staging.mode}`) }),
    st.catchup ? 'catchup' : null,
  ].filter(Boolean).join(' · ');

  const inp = 'border border-gray-300 rounded px-2 py-1 text-sm bg-white';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
      <form
        onSubmit={save}
        className="bg-white rounded shadow-lg w-[640px] max-w-[95vw] h-[640px] max-h-[90vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800 truncate">
              {isNew ? t('layout:explorer.newTitle') : t('dialog:settings.title', { name: workflow.name })}
            </div>
            {runEditable && <div className="text-xs text-gray-500">{t('dialog:settings.subtitle')}</div>}
          </div>
          <button type="button" className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('common:ui.close')}>✕</button>
        </div>

        <div className="flex-1 min-h-0 p-4 overflow-auto text-sm">
          <Section>{t('dialog:settings.general')}</Section>
          <Row label={t('layout:explorer.name')}>
            <input autoFocus className={`${inp} w-full`} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('layout:explorer.namePlaceholder')} required />
          </Row>
          <Row label={t('layout:explorer.folder')}>
            <input className={`${inp} w-full`} list="wf-folder-options" value={folder} onChange={(e) => setFolder(e.target.value)} placeholder={t('layout:explorer.folderPlaceholder')} />
            <datalist id="wf-folder-options">
              {folderOptions.map((f) => <option key={f} value={f} />)}
            </datalist>
          </Row>
          <Row label={t('layout:explorer.description')}>
            <textarea className={`${inp} w-full`} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('layout:explorer.descriptionPlaceholder')} />
          </Row>

          {runEditable && (
            <>
              <Section>{t('dialog:settings.schedule')}</Section>
              <Row label={t('dialog:settings.interval')}>
                <div className="flex items-center gap-2">
                  <select className={inp} value={custom ? '__custom' : (st.schedule ?? '')} onChange={(e) => { if (e.target.value === '__custom') { setCustom(true); } else { setCustom(false); patch({ schedule: e.target.value || null }); } }}>
                    {SCHEDULE_PRESETS.map((p) => <option key={String(p.value)} value={p.value ?? ''}>{t(p.labelKey)}</option>)}
                    <option value="__custom">{t('dialog:settings.customCron')}</option>
                  </select>
                  {custom && <input className={`${inp} font-mono flex-1 ${cronOk ? '' : 'border-red-400'}`} value={st.schedule ?? ''} placeholder={t('dialog:settings.cronPlaceholder')} onChange={(e) => patch({ schedule: e.target.value || null })} />}
                </div>
                <div className="text-[0.6875rem] text-gray-500 mt-0.5">
                  {describeSchedule(st.schedule)}{st.schedule ? t('dialog:settings.timezoneOf', { tz: effectiveTimeZone() }) : ''}
                </div>
                {st.schedule && <div className="text-[0.6875rem] text-amber-600 mt-0.5">{t('dialog:settings.scheduleWarn')}</div>}
              </Row>
              {st.schedule && (
                <Row label={t('dialog:settings.startDate')} hint={t('dialog:settings.startDateHint')}>
                  <input type="date" className={inp} value={st.startDate} onChange={(e) => patch({ startDate: e.target.value })} />
                </Row>
              )}

              {/* 워크플로 변수 — 이 워크플로의 모든 노드가 ${이름} 으로 쓴다 (서브플로·노드 변수가 같은 이름이면 그쪽이 이긴다) */}
              <div className="mt-3">
                <ParamsEditor title={t('dialog:settings.wfVars')} items={st.params || []} onChange={(items) => patch({ params: items })} />
                <div className="text-[0.6875rem] text-gray-500 -mt-2 mb-1">{t('dialog:settings.wfVarsHint')} <code>{'${name}'}</code> {t('dialog:settings.wfVarsHintTail')}</div>
              </div>

              {/* 상세 — 기본은 접힘. 요약을 머리에 보여 펼치지 않아도 무엇이 켜져 있는지 안다 */}
              <button
                type="button"
                className="mt-3 w-full flex items-center gap-2 px-2 py-1.5 rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 text-left"
                onClick={() => setDetailOpen((v) => !v)}
                aria-expanded={detailOpen}
              >
                <span className={classNames('tree-chevron text-gray-500', detailOpen && 'open')}>▸</span>
                <span className="text-xs font-semibold text-gray-700 shrink-0">{t('dialog:settings.detail')}</span>
                <span className="text-[0.6875rem] text-gray-500 truncate">{detailSummary}</span>
              </button>
              {detailOpen && (
                <div className="pl-2 pt-1">
                  <Row label={t('dialog:settings.catchupLabel')}>
                    <label className="flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" checked={st.catchup} onChange={(e) => patch({ catchup: e.target.checked })} />{t('dialog:settings.catchup')}</label>
                  </Row>

                  <Section>{t('dialog:settings.retrySection')}</Section>
                  <Row label={t('dialog:settings.retries')}>
                    <div className="flex items-center gap-3">
                      <input type="number" min={0} className={`${inp} w-20`} value={st.retries} onChange={(e) => patch({ retries: Math.max(0, Number(e.target.value) || 0) })} />
                      <span className="text-xs text-gray-600">{t('dialog:settings.retryDelay')}</span>
                      <input type="number" min={0} className={`${inp} w-20`} value={st.retryDelayMin} onChange={(e) => patch({ retryDelayMin: Math.max(0, Number(e.target.value) || 0) })} />
                    </div>
                  </Row>
                  <Row label={t('dialog:settings.timeout')} hint={t('dialog:settings.timeoutHint')}>
                    <input type="number" min={1} className={`${inp} w-24`} value={st.timeoutMin ?? ''} placeholder={t('dialog:settings.none')} onChange={(e) => patch({ timeoutMin: e.target.value === '' ? null : Math.max(1, Number(e.target.value) || 1) })} />
                  </Row>

                  <Section>{t('dialog:settings.notify')}</Section>
                  <Row label={t('dialog:settings.when')}>
                    <div className="flex items-center gap-4 text-xs text-gray-700">
                      <label className="flex items-center gap-1.5"><input type="checkbox" checked={st.notify.onFailure} onChange={(e) => patchN({ onFailure: e.target.checked })} />{t('dialog:settings.onFailure')}</label>
                      <label className="flex items-center gap-1.5"><input type="checkbox" checked={st.notify.onSuccess} onChange={(e) => patchN({ onSuccess: e.target.checked })} />{t('dialog:settings.onSuccess')}</label>
                    </div>
                  </Row>
                  <Row label={t('dialog:settings.webhook')} hint={t('dialog:settings.webhookHint')}>
                    <input className={`${inp} w-full font-mono`} value={st.notify.webhook} placeholder="https://hooks.slack.com/services/…" onChange={(e) => patchN({ webhook: e.target.value })} />
                  </Row>
                  <Row label={t('dialog:settings.email')} hint={t('dialog:settings.emailHint')}>
                    <input className={`${inp} w-full`} value={st.notify.emails} placeholder="ops@example.com, dba@example.com" onChange={(e) => patchN({ emails: e.target.value })} />
                  </Row>

                  <Section>{t('dialog:settings.staging')}</Section>
                  <Row label={t('dialog:settings.mode')} hint={t('dialog:settings.modeHint')}>
                    <select className={inp} value={st.staging.mode} onChange={(e) => patchS({ mode: e.target.value as any })}>
                      <option value="auto">{t('dialog:settings.modeAuto')}</option>
                      <option value="file">{t('dialog:settings.modeFile')}</option>
                      <option value="table">{t('dialog:settings.modeTable')}</option>
                      <option value="xcom">{t('dialog:settings.modeXcom')}</option>
                    </select>
                  </Row>
                  {st.staging.mode === 'auto' && (
                    <Row label={t('dialog:settings.threshold')}>
                      <input type="number" min={1} className={`${inp} w-28`} value={st.staging.thresholdRows} onChange={(e) => patchS({ thresholdRows: Math.max(1, Number(e.target.value) || 1) })} />
                    </Row>
                  )}
                  {st.staging.mode === 'table' && (
                    <Row label={t('dialog:settings.stagingConn')} hint={t('dialog:settings.stagingConnHint')}>
                      <select className={inp} value={st.staging.connection} onChange={(e) => patchS({ connection: e.target.value })}>
                        <option value="">{t('dialog:settings.select')}</option>{connections.map((c: any) => <option key={c.connection_id} value={c.connection_id}>{c.connection_id}{c.conn_type ? ` (${c.conn_type})` : ''}</option>)}
                      </select>
                    </Row>
                  )}
                  {st.staging.mode !== 'xcom' && (
                    <Row label={t('dialog:settings.keepHours')} hint={t('dialog:settings.keepHoursHint')}>
                      <input type="number" min={1} className={`${inp} w-24`} value={st.staging.keepHours} onChange={(e) => patchS({ keepHours: Math.max(1, Number(e.target.value) || 1) })} />
                    </Row>
                  )}

                  <Section>{t('dialog:settings.sqlTag')}</Section>
                  <Row label={t('dialog:settings.sqlCommentLabel')} hint={t('dialog:settings.sqlCommentHint')}>
                    <label className="flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" checked={st.sqlComment !== false} onChange={(e) => patch({ sqlComment: e.target.checked })} />{t('dialog:settings.sqlComment')}</label>
                  </Row>
                </div>
              )}
            </>
          )}
        </div>

        <div className="shrink-0 px-4 py-2 border-t border-gray-200 flex justify-end gap-2">
          <button type="button" className="px-3 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 text-gray-700" onClick={onClose}>{t('common:ui.cancel')}</button>
          <button type="submit" className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50" disabled={!canSave}>
            {isNew ? t('layout:explorer.create') : t('common:ui.apply')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WorkflowSettingsDialog;
